import { Card, Colors, IBreadcrumbProps, IResizeEntry, ResizeSensor } from "@blueprintjs/core";
import { ErrorBoundary, Loading } from "nsitools-react";
import * as React from "react";
import { useHistory, useLocation } from "react-router";

import { ERoutes } from "../ERoutes";
import { useBreadcrumbs, useDetailHistoryContext } from "../contexts";
import styled from "styled-components";
import { ETLCodes } from "../locales";
import useComponentSize from "@rehooks/component-size";
import { useScroll } from "ahooks";
import { StickyContainer, Sticky } from "react-sticky";
export interface ICustomBreadcrumbProps extends Omit<IBreadcrumbProps, "href"> {
  route?: string | ERoutes;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: 100%;
  align-self: stretch;
  //padding: 0.5em;
  overflow: auto;
`;

const FixedStickyContainer = styled.div`
  //position: fixed;
  padding: 0rem;
  background-color: ${Colors.WHITE};
  z-index: 15;
  //margin: -0.5rem -0.5rem 0.5rem -0.5rem;
`;

interface IPageBase extends React.PropsWithChildren {
  breadCrumbs?: ICustomBreadcrumbProps[];
  isLoading?: boolean;
  withCard?: boolean;
  actionHistory?: {
    tlCode: ETLCodes;
    name: string;
    id: number;
  };
  stickyContent?: () => React.ReactNode;
}
export const PageBase: React.FunctionComponent<IPageBase> = ({
  breadCrumbs,
  children,
  isLoading = false,
  withCard = true,
  actionHistory: newActionHistory,
  stickyContent = null,
}) => {
  const { setBaseBreadcrumb, setDetailsBreadcrumb } = useBreadcrumbs();
  const { addToActionHistory, actionHistory } = useDetailHistoryContext();
  const history = useHistory();
  const scrollPosition = useScroll(document);

  const { pathname } = useLocation();

  React.useEffect(() => {
    if (newActionHistory && newActionHistory.id > 0) {
      addToActionHistory({
        route: pathname,
        tlCode: newActionHistory.tlCode,
        name: newActionHistory.name,
      });
    }
  }, [newActionHistory, addToActionHistory, pathname, actionHistory]);

  React.useEffect(() => {
    setDetailsBreadcrumb([]);
  }, [setDetailsBreadcrumb]);

  React.useEffect(() => {
    if (breadCrumbs) {
      const nextBr: IBreadcrumbProps[] = breadCrumbs.map((br) =>
        br.route
          ? {
              ...br,
              onClick: () => {
                if (br.route) {
                  history.push(br.route);
                }
              },
            }
          : { ...br }
      );

      setBaseBreadcrumb(nextBr);
    }
  }, [breadCrumbs, history, setBaseBreadcrumb]);

  const fixedStickyContainerRef = React.useRef<HTMLDivElement>(null);
  let fixedStickyContainerDimensions = useComponentSize(fixedStickyContainerRef);

  const Wrapper = React.useMemo(() => {
    if (withCard)
      return (props: any) => (
        <Card elevation={1} style={{ padding: "0.5em", margin: "0.5rem", minHeight: "100%" }}>
          {props.children}
        </Card>
      );
    else return (props: any) => <>{props.children}</>;
  }, [withCard]);

  const [stickyTopPosition, setStickyTopPosition] = React.useState(0);
  const [stickyLeftPosition, setStickyLeftPosition] = React.useState(0);

  const onRootResize = React.useCallback(
    (entries: IResizeEntry[]) => {
      if (entries.length <= 0) return;
      const lastEntry = entries.reverse().find((e) => e.target);

      const { top, left } = lastEntry.target.getBoundingClientRect();
      const finalTop = top + (scrollPosition?.top ?? 0);
      const finalLeft = left + (scrollPosition?.left ?? 0);

      setStickyTopPosition(finalTop);
      setStickyLeftPosition(finalLeft);
    },
    [scrollPosition, stickyLeftPosition, stickyTopPosition]
  );

  return (
    <ResizeSensor onResize={onRootResize}>
      <Container>
        <div
          style={{
            paddingTop: fixedStickyContainerDimensions.height,
            flex: 1,
            flexDirection: "column",
          }}
        >
          <StickyContainer>
            {stickyContent && (
              <Sticky>
                {({ style, isSticky }) => (
                  <FixedStickyContainer style={!isSticky ? { ...style } : { ...style, marginTop: "75px" }}>
                    {stickyContent()}
                  </FixedStickyContainer>
                )}
              </Sticky>
            )}
            <Wrapper>
              <ErrorBoundary>
                <Loading isLoading={isLoading}>{children}</Loading>
              </ErrorBoundary>
            </Wrapper>
          </StickyContainer>
        </div>
      </Container>
    </ResizeSensor>
  );
};
